import React, { type ReactNode, useMemo } from 'react'

import type { TagProps } from '@fourthwall/components'
import { Button, ButtonClean, Colors, Dropdown, Tag, ToolbarButton } from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'

import * as S from './ConnectBar.styled'
import type { LogoStyle } from './ConnectBar.types'

export type ConnectBarProps = {
  logoSrc: string
  logoAlt?: string
  logoStyle?: LogoStyle
  description?: string
  tag?: TagProps | null
  info?: ReactNode
  updateInfo?: ReactNode
  connectButtonLabel?: string
  updateButtonLabel?: string
  disconnectButtonLabel?: string
  reconnectButtonLabel?: string
  settingsUri?: string
  settingsLabel?: string
  footer?: ReactNode
  disabled?: boolean
  isConnecting?: boolean
  onConnect?: () => void
  onDisconnect?: () => void
  onReconnect?: () => void
  onUpdate?: () => void
}

export const ConnectBar = ({
  logoSrc,
  logoAlt,
  logoStyle = 'normal',
  description,
  tag,
  info,
  updateInfo,
  connectButtonLabel = 'Connect',
  updateButtonLabel = 'Update',
  disconnectButtonLabel = 'Disconnect',
  reconnectButtonLabel = 'Reconnect',
  settingsUri,
  settingsLabel = 'Settings',
  footer,
  disabled = false,
  isConnecting = false,
  onConnect,
  onDisconnect,
  onReconnect,
  onUpdate,
}: ConnectBarProps) => {
  const tagProps = ((): TagProps | undefined => {
    if (tag) return tag
    if (tag === null) return undefined

    if (!onConnect && onDisconnect) {
      return { label: 'Connected', appearance: 'success' }
    }
  })()

  const dropdownContent = useMemo(
    () => <Dropdown.Item label="Disconnect" onClick={onDisconnect} />,
    [onDisconnect],
  )

  return (
    <S.Container>
      <S.Main>
        {logoSrc && (
          <S.LogoContainer $logoStyle={logoStyle}>
            <img src={logoSrc} alt={logoAlt} />
          </S.LogoContainer>
        )}
        <S.Content>
          <S.DescriptionAndTagContainer>
            {description && <S.Description>{description}</S.Description>}
            {tagProps && (
              <S.TagWrapper>
                <Tag {...tagProps} display="inline-flex" />
              </S.TagWrapper>
            )}
          </S.DescriptionAndTagContainer>
          {info && <S.InfoContainer>{info}</S.InfoContainer>}
        </S.Content>
        <S.Buttons>
          {settingsUri && <ButtonClean label={settingsLabel} href={settingsUri} target="_blank" />}
          {onUpdate && (
            <Button
              label={updateButtonLabel}
              appearance="primary"
              size="small"
              disabled={disabled}
              onClick={onUpdate}
            />
          )}
          {onDisconnect && onUpdate && (
            <Dropdown content={dropdownContent}>
              <ToolbarButton icon={DotsHorizontalIcon} color={Colors.GS600} />
            </Dropdown>
          )}
          {onDisconnect && !onUpdate && (
            <ButtonClean label={disconnectButtonLabel} disabled={disabled} onClick={onDisconnect} />
          )}
          {onConnect && !onUpdate && (
            <Button
              label={connectButtonLabel}
              appearance="primary"
              size="small"
              loading={isConnecting}
              disabled={disabled}
              onClick={onConnect}
            />
          )}
          {onReconnect && (
            <ButtonClean label={reconnectButtonLabel} disabled={disabled} onClick={onReconnect} />
          )}
        </S.Buttons>
      </S.Main>
      {footer && <S.Footer>{footer}</S.Footer>}
      {updateInfo && onUpdate && <S.UpgradeInfo>{updateInfo}</S.UpgradeInfo>}
    </S.Container>
  )
}
